import { useRef, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import DatePicker from 'react-datepicker';
import { isCurrentWeek } from 'Helper/isCurrentWeek';
import { CalendarIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import { TextRegular, TextSemiBold } from 'UiKit/typography';
import Tooltip from 'UiKit/tooltip';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './styles.module.css';

const formatDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const InputDatepicker = ({
  label,
  placeholder = 'DD/MM/YYYY',
  onChange,
  value,
  isDisabled,
  error,
  isRequired,
  register,
  margin,
  className,
  minDate,
  maxDate,
  titleClassname,
  dateFormat = 'dd/MM/yyyy',
  startDate = '',
  endDate = '',
  selectsRange = false,
  shouldCloseOnSelect = true,
  datepickerClassname = '',
  excludeDates = [],
  selectsMultiple = false,
  selectedDates = [],
  infoMessage,
  calendarStatuses = [],
  getCalendarDayTooltip,
}) => {
  const isError = !isEmpty(error);
  const ref = useRef(null);
  const isEmptyValue = !value && (!startDate || !endDate) && isEmpty(selectedDates);
  const onOpenDatePicker = () => ref.current.setOpen(true);
  const hasCalendarStatuses = calendarStatuses.length > 0;

  const getDayClassName = (date) => (isCurrentWeek(date) ? 'highlight-week' : '');

  const { statusMap, highlightDates } = useMemo(() => {
    const map = {};
    const blocked = [];
    const byClass = {};
    calendarStatuses.forEach((entry) => {
      map[entry.date] = entry;
      const d = new Date(`${entry.date}T00:00:00`);
      if (entry.blocked) blocked.push(d);
      if (entry.dayClass) {
        byClass[entry.dayClass] = byClass[entry.dayClass] || [];
        byClass[entry.dayClass].push(d);
      }
    });
    const highlights = Object.entries(byClass).map(([cls, dates]) => ({ [cls]: dates }));
    if (blocked.length) highlights.push({ 'calendar-day--blocked': blocked });
    return { statusMap: map, highlightDates: highlights };
  }, [calendarStatuses]);

  const getStatusEntry = (date) => statusMap[formatDateKey(date)];

  const isDateSelectable = (date) => {
    const entry = getStatusEntry(date);
    return !entry || !entry.blocked;
  };

  const clampedMaxDate = useMemo(() => {
    if (!selectsRange || !hasCalendarStatuses || !startDate || endDate) return maxDate;
    const sortedBlocked = calendarStatuses
      .filter((e) => e.blocked)
      .map((e) => new Date(`${e.date}T00:00:00`))
      .filter((d) => d > startDate)
      .sort((a, b) => a - b);
    if (!sortedBlocked.length) return maxDate;
    const dayBefore = new Date(sortedBlocked[0]);
    dayBefore.setDate(dayBefore.getDate() - 1);
    return maxDate && maxDate < dayBefore ? maxDate : dayBefore;
  }, [selectsRange, hasCalendarStatuses, startDate, endDate, maxDate, calendarStatuses]);

  const handleChange = (value) => {
    if (!onChange) return;
    if (selectsRange && hasCalendarStatuses && Array.isArray(value)) {
      const [start, end] = value;
      if (start && end) {
        const rangeMin = start <= end ? start : end;
        const rangeMax = start <= end ? end : start;
        const spansBlocked = calendarStatuses.some((entry) => {
          if (!entry.blocked) return false;
          const d = new Date(`${entry.date}T00:00:00`);
          return d >= rangeMin && d <= rangeMax;
        });
        if (spansBlocked) {
          onChange([end, null]);
          return;
        }
      }
    }
    onChange(value);
  };

  const renderDayContents = (dayOfMonth, date) => {
    if (!hasCalendarStatuses) return <span>{dayOfMonth}</span>;
    const entry = getStatusEntry(date);
    if (!entry || !entry.blocked || !entry.coverStyle) return <span>{dayOfMonth}</span>;

    const tooltipData = getCalendarDayTooltip ? getCalendarDayTooltip(entry) : null;

    return (
      <span className={styles['calendar-day-cover']} style={entry.coverStyle}>
        {tooltipData ? (
          <Tooltip htmlComponent={tooltipData.htmlComponent} width="220px" placement="center">
            <span>{dayOfMonth}</span>
          </Tooltip>
        ) : (
          <span>{dayOfMonth}</span>
        )}
      </span>
    );
  };

  return (
    <Box
      margin={margin}
      className={`${className || ''}
        ${styles['datepicker-component__container']} 
        ${isDisabled && styles['datepicker-component__container--disabled']} 
        ${isError && styles['datepicker-component__container--error']}
      `}
    >
      <Condition statement={label}>
        <TextSemiBold className={`${styles['text__title']} ${titleClassname}`}>
          {label}{' '}
          <Condition statement={isRequired}>
            <span>*</span>
          </Condition>
        </TextSemiBold>
      </Condition>

      <Condition statement={infoMessage}>
        <TextRegular className={styles['info__text']}>{infoMessage}</TextRegular>
      </Condition>

      <Box className={`datepicker-custom__container`}>
        <DatePicker
          ref={ref}
          placeholderText={placeholder}
          selected={value}
          onChange={handleChange}
          className={`${styles['datepicker-custom']} ${!isEmptyValue && styles['datepicker-custom__active']} ${datepickerClassname}`}
          calendarClassName="datepicker__calendar"
          formatWeekDay={(day) => day.substring(0, 1)}
          dateFormat={dateFormat}
          minDate={minDate}
          maxDate={clampedMaxDate}
          showTimeSelect={false}
          showTimeInput={false}
          shouldCloseOnSelect={shouldCloseOnSelect}
          selectsRange={selectsRange}
          selectedDates={selectedDates}
          selectsMultiple={selectsMultiple}
          excludeDates={excludeDates}
          startDate={startDate}
          endDate={endDate}
          disabled={isDisabled}
          dayClassName={getDayClassName}
          highlightDates={highlightDates}
          filterDate={hasCalendarStatuses ? isDateSelectable : undefined}
          renderDayContents={hasCalendarStatuses ? renderDayContents : undefined}
          {...register}
        />

        <CalendarIcon className="calendar__icon" color="var(--color-neutral70)" size="22px" onClick={onOpenDatePicker} />
      </Box>
    </Box>
  );
};

export default InputDatepicker;
